import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AvatarGroup, MoneyInput, Skeleton } from "../ui";
import { useToast } from "../contexts/ToastContext";
import { api, ApiError } from "../lib/api";
import { currencySymbol, formatMoney } from "../lib/format";
import { ExpenseOut, MeOut, MemberOut, RecordedOut } from "../lib/schemas";
import { useBackButton, useMainButton, webApp, haptic } from "../lib/telegram";

export default function AddExpense() {
  const { id } = useParams<{ id: string }>();
  const groupId = Number(id);
  const nav = useNavigate();
  const showToast = useToast();

  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [payerId, setPayerId] = useState<number | null>(null);
  const [splitIds, setSplitIds] = useState<number[]>([]);
  const [splitTouched, setSplitTouched] = useState(false);

  const [members, setMembers] = useState<MemberOut[]>([]);
  const [currency, setCurrency] = useState("USD");
  const [loadError, setLoadError] = useState<string | null>(null);
  const [recentDescriptions, setRecentDescriptions] = useState<string[]>([]);

  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [duplicatePending, setDuplicatePending] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const [memberList, me, expenses] = await Promise.all([
          api<MemberOut[]>("GET", `/chats/${groupId}/members`),
          api<MeOut>("GET", "/me"),
          api<ExpenseOut[]>("GET", `/chats/${groupId}/expenses?limit=50`).catch(() => [] as ExpenseOut[]),
        ]);
        if (cancelled) return;
        setMembers(memberList);
        const self = memberList.find(
          (m) => m.telegram_id === me.user.telegram_id,
        );
        const defaultPayerId = self?.id ?? memberList[0]?.id ?? null;
        setPayerId(defaultPayerId);
        const defaultSplit = memberList
          .filter((m) => m.id !== defaultPayerId)
          .map((m) => m.id);
        setSplitIds(defaultSplit.length > 0 ? defaultSplit : (defaultPayerId !== null ? [defaultPayerId] : []));
        const myGroup = me.groups.find((g) => g.id === groupId);
        if (myGroup) setCurrency(myGroup.currency);
        const recentDescs = [...new Set(
          expenses.filter(e => e.kind !== "settlement").map(e => e.description)
        )].slice(0, 5);
        setRecentDescriptions(recentDescs);
      } catch (e) {
        if (!cancelled)
          setLoadError(e instanceof Error ? e.message : "Failed to load");
      }
    }
    load();
    return () => { cancelled = true; };
  }, [groupId]);

  useEffect(() => {
    if (amount !== "" || description !== "") {
      webApp.enableClosingConfirmation();
    } else {
      webApp.disableClosingConfirmation();
    }
    return () => webApp.disableClosingConfirmation();
  }, [amount, description]);

  const amountNum = parseFloat(amount);
  const isValidAmount = !isNaN(amountNum) && amountNum > 0;
  const isReady = isValidAmount && description.trim().length > 0;

  const splitLabel =
    members.length > 0 && splitIds.length === members.length
      ? "Everyone"
      : payerId !== null && splitIds.length === members.length - 1 && !splitIds.includes(payerId)
        ? "Everyone except payer"
        : `${splitIds.length} ${splitIds.length === 1 ? "person" : "people"}`;

  const btnText = duplicatePending
    ? "Record anyway"
    : isReady
      ? `Confirm ${formatMoney(amountNum, currency)}`
      : "Confirm";

  const submit = useCallback(async () => {
    if (!isReady || submitting || payerId === null) return;
    setSubmitting(true);
    setFormError(null);
    webApp.MainButton.showProgress(false);
    try {
      const url = `/chats/${groupId}/expenses${duplicatePending ? "?force=true" : ""}`;
      const raw = await api<unknown>("POST", url, {
        json: {
          amount: amountNum,
          description: description.trim(),
          payer_user_id: payerId,
          participant_user_ids: splitIds,
        },
      });
      const result = RecordedOut.parse(raw);
      let msg = "Done";
      if (result.kind === "expense_recorded") {
        msg = "Recorded ✓";
      } else if (result.kind === "pending_created") {
        msg = "Sent for approval";
      }
      setDuplicatePending(false);
      haptic.success();
      showToast(msg, { type: "success" });
      nav(`/groups/${groupId}`, { replace: true });
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        setDuplicatePending(true);
      } else {
        haptic.error();
        setFormError(e instanceof Error ? e.message : "Something went wrong");
      }
    } finally {
      setSubmitting(false);
      webApp.MainButton.hideProgress();
    }
  }, [isReady, submitting, payerId, amountNum, description, splitIds, groupId, duplicatePending, showToast, nav]);

  const handleBack = useCallback(() => nav(`/groups/${groupId}`), [nav, groupId]);
  useBackButton(handleBack);
  const handleSubmit = useCallback(() => { void submit(); }, [submit]);
  useMainButton(btnText, isReady && !submitting, handleSubmit);

  if (loadError) {
    return (
      <div className="min-h-screen bg-bg flex items-center justify-center px-4">
        <div className="text-center">
          <p className="text-danger mb-4">{loadError}</p>
          <button
            type="button"
            onClick={() => nav(`/groups/${groupId}`)}
            className="border border-fg/30 rounded-xl px-5 py-2.5 text-fg font-sans text-sm font-semibold active:opacity-70 transition-opacity"
          >
            Go back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bg px-4 pt-6 pb-32">
      <h1 className="font-sans text-base font-semibold text-fg mb-6 text-center">
        Add Expense
      </h1>

      {/* Duplicate inline banner */}
      {duplicatePending && (
        <div className="bg-warning/10 border border-warning/40 rounded-2xl px-4 py-3 text-sm text-fg mb-4">
          ⚠ Looks like a duplicate. Tap to record anyway.
        </div>
      )}

      {/* Amount */}
      <div className="mb-6">
        <MoneyInput
          label="Amount"
          currencySymbol={currencySymbol(currency)}
          placeholder="0.00"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          className="text-lg"
        />
        {/* Quick-amount tags */}
        <div className="flex gap-2 flex-wrap mt-3">
          {[5, 10, 20, 50].map(v => (
            <button
              key={v}
              type="button"
              onClick={() => setAmount(String(v))}
              className="border border-hairline text-muted font-sans text-[13px] font-medium rounded-full px-3.5 py-2 active:bg-surface transition-colors"
            >
              {currencySymbol(currency)}{v}
            </button>
          ))}
        </div>
      </div>

      {/* Description */}
      <div className="mb-6">
        <label className="block font-sans text-xs font-medium text-muted mb-3">
          Description
        </label>
        <input
          type="text"
          placeholder="e.g. Coffee"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          className="w-full px-0 py-3 bg-transparent text-fg border-b border-hairline focus:outline-none focus:border-fg placeholder:text-faint"
        />
        {/* Recent description tags */}
        {recentDescriptions.length > 0 && description === "" && (
          <div className="flex gap-2 flex-wrap mt-3">
            {recentDescriptions.map(d => (
              <button
                key={d}
                type="button"
                onClick={() => setDescription(d)}
                className="border border-hairline text-muted font-sans text-[13px] font-medium rounded-full px-3.5 py-2 active:bg-surface transition-colors max-w-[150px] truncate"
              >
                {d}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Paid by */}
      <div className="mb-6">
        <label className="block font-sans text-xs font-medium text-muted mb-3">
          Paid by
        </label>
        {members.length === 0 ? (
          <div className="flex gap-3">
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-10 w-10 rounded-full" />
            ))}
          </div>
        ) : (
          <AvatarGroup
            members={members.map(m => ({ id: m.id, display_name: m.first_name }))}
            selectedIds={payerId !== null ? [payerId] : []}
            onToggle={(id) => {
              const newPayerId = Number(id);
              setPayerId(newPayerId);
              if (!splitTouched) {
                const next = members.filter(m => m.id !== newPayerId).map(m => m.id);
                setSplitIds(next.length > 0 ? next : [newPayerId]);
              }
            }}
            size="md"
          />
        )}
      </div>

      {/* Split between */}
      <div className="mb-6">
        <label className="block font-sans text-xs font-medium text-muted mb-3">
          Split between
        </label>
        {members.length === 0 ? (
          <div className="flex gap-3">
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-10 w-10 rounded-full" />
            ))}
          </div>
        ) : (
          <AvatarGroup
            members={members.map(m => ({ id: m.id, display_name: m.first_name }))}
            selectedIds={splitIds}
            onToggle={(id) => {
              const numId = Number(id);
              setSplitTouched(true);
              setSplitIds(prev =>
                prev.includes(numId)
                  ? prev.length > 1 ? prev.filter(x => x !== numId) : prev
                  : [...prev, numId]
              );
            }}
            size="md"
          />
        )}
        <p className="text-muted text-xs mt-2">{splitLabel}</p>
      </div>

      {formError && (
        <p className="text-danger text-sm text-center mt-2">{formError}</p>
      )}
    </div>
  );
}
